import type { ConnectwareError, CybusService, CybusServiceDeviation, StatusType } from '../../../domain'

import type { AllSubscriptionsTypes, PageSubscriptionsTypes, SingleSubscriptionsTypes } from '../../../application'

type SupportedType<T> = Extract<keyof (PageSubscriptionsTypes & AllSubscriptionsTypes & SingleSubscriptionsTypes), T>

export type StatusSupportedType = SupportedType<
    'connection' | 'connections' | 'endpoint' | 'endpoints' | 'mapping' | 'mappings' | 'nodes' | 'server' | 'servers'
>

export interface StatusTypeFetcher {
    fetchStatusType(type: StatusSupportedType, resourceId: string): Promise<StatusType>
}

export interface StatusTypeSubscription {
    useCachedStatusType(type: StatusSupportedType, id: string): StatusType | ConnectwareError | null
    /**
     * @param handler called once when setup, and then further when there are changes to that resource state
     */
    subscribeToStatusType(type: StatusSupportedType, id: string, handler: VoidFunction): Promise<VoidFunction>
}

export type ServiceDeviationsSupportedType = SupportedType<
    'service' | 'services' | 'connections' | 'endpoints' | 'servers' | 'nodes' | 'mappings' | 'resourceDeviation'
>

export type ServicesDeviationsSupportedType = SupportedType<
    'deviatedServices' | 'serviceDeviations' | 'services' | 'connections' | 'endpoints' | 'servers' | 'nodes' | 'mappings' | 'resourceDeviation'
>

export type ServicesResolvedDeviationsSupportedType = SupportedType<'resolvedServiceDeviations'>

export interface DeviationFetcher {
    /**
     * This method does not detect if the service exists
     * Only if, for that given service, there is a deviation
     */
    fetchServiceDeviationsCount(type: ServiceDeviationsSupportedType, serviceId: CybusService['id']): Promise<number>
}

export interface DeviationsSubscription {
    useCachedServiceDeviationsCount(type: ServiceDeviationsSupportedType, serviceId: CybusService['id']): number | ConnectwareError | null

    /**
     * @param handler called once when setup, and then further when there are changes to the deviations count of the given service
     */
    subscribeToServiceDeviationsCount(type: ServiceDeviationsSupportedType, serviceId: CybusService['id'], handler: VoidFunction): Promise<VoidFunction>

    /**
     * @param handler called once when setup, and then further when there are changes to the deviations count of all services
     */
    subscribeToServicesDeviationsCount(type: ServicesDeviationsSupportedType, handler: VoidFunction): Promise<VoidFunction>

    /**
     * @param handler called when there are deviation resolutions or errors associated with the subscription
     */
    subscribeToResolvedDeviations(
        type: ServicesResolvedDeviationsSupportedType,
        handler: (deviation: CybusServiceDeviation | ConnectwareError) => void
    ): Promise<VoidFunction>
}

export type RSTAdapter =
    & Pick<DeviationFetcher, 'fetchServiceDeviationsCount'>
    & Pick<DeviationsSubscription, 'subscribeToServiceDeviationsCount' | 'subscribeToServicesDeviationsCount' | 'subscribeToResolvedDeviations'>
    & Pick<StatusTypeFetcher, 'fetchStatusType'>
    & Pick<StatusTypeSubscription, 'subscribeToStatusType'>
